import React, { useEffect, useMemo, useState } from 'react'
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ClockIcon,
  Cog6ToothIcon,
  DocumentArrowDownIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ScaleIcon,
  ShieldCheckIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'

import {
  Badge,
  Button,
  Input,
  Label,
  Page,
  PageBody,
  PageHeader,
  TabularDataViewer,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  buttonVariants,
} from '../../components/ui'
import { useAuth } from '../../stores/useAuth'
import { cn } from '../../shared/lib/cn'
import { workflowSteps } from './data'
import {
  useBankReconciliationAvailableConnectors,
  useBankReconciliationDataSourceConfig,
  useBankReconciliationWorkpaper,
  useClearBankReconciliationDataSource,
  useRunBankReconciliationAgent,
  useSetBankReconciliationDataSource,
  useTestBankReconciliationDataSource,
} from './api'
import type {
  AnomalyFlag,
  BankReconciliationConnectorOption,
  BankReconciliationDataSourceConfig,
  DecisionItem,
  DecisionsState,
  DecisionStatus,
  JournalLine,
  Kpi,
  ReconciliationRow,
  TabularRows,
} from './types'
import {
  areAllDecisionsResolved,
  buildInitialDecisions,
  computeDecisionCounts,
  computeResidualVariance,
  downloadTextFile,
  formatCurrency,
  formatSignedCurrency,
  generateAuditTrailCsv,
  generateNetSuiteCsv,
  getCloseStatus,
} from './utils'

type FieldErrors = Record<number, string | undefined>

const statusClasses: Record<DecisionStatus, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
  modified: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800',
}

const statusLabels: Record<DecisionStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  modified: 'Modified',
  rejected: 'Rejected',
}

const DEFAULT_REDSHIFT_SCHEMA = 'bank_reconciliation'

type ConfigMessage = {
  tone: 'success' | 'error' | 'info'
  text: string
}

function getErrorMessage(error: Error) {
  const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
  if (typeof detail === 'string') return detail
  return error.message || 'Request failed'
}

function formatMode(mode?: string | null) {
  if (mode === 'saved') return 'Saved connector'
  if (mode === 'env_fallback') return 'Environment fallback'
  if (mode === 'unconfigured') return 'Unconfigured'
  return mode || 'Unknown'
}

function getConfigVariant(config?: BankReconciliationDataSourceConfig): React.ComponentProps<typeof Badge>['variant'] {
  if (config?.mode === 'saved') return 'success'
  if (config?.mode === 'env_fallback') return 'warning'
  return 'secondary'
}

function getMessageClasses(tone: ConfigMessage['tone']) {
  if (tone === 'success') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200'
  }
  if (tone === 'error') {
    return 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200'
  }
  return 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-200'
}

function DataSourceConfigPanel({
  config,
  configLoading,
  connectors,
  connectorsLoading,
  selectedConnectorId,
  schema,
  canWrite,
  message,
  saving,
  testing,
  clearing,
  onConnectorChange,
  onSchemaChange,
  onTest,
  onSave,
  onClear,
}: {
  config?: BankReconciliationDataSourceConfig
  configLoading: boolean
  connectors: BankReconciliationConnectorOption[]
  connectorsLoading: boolean
  selectedConnectorId: string
  schema: string
  canWrite: boolean
  message: ConfigMessage | null
  saving: boolean
  testing: boolean
  clearing: boolean
  onConnectorChange: (connectorId: string) => void
  onSchemaChange: (schema: string) => void
  onTest: () => void
  onSave: () => void
  onClear: () => void
}) {
  const activeConnector = connectors.find(
    connector => connector.connector_id === selectedConnectorId,
  )
  const connected = Boolean(config?.is_connected)
  const statusText = !canWrite
    ? 'Configuration restricted'
    : configLoading
    ? 'Checking source'
    : connected
      ? config?.connector_name || 'Connected'
      : 'No Redshift connector selected'
  const statusTone = connected
    ? 'bg-emerald-500'
    : configLoading
      ? 'bg-sky-500'
      : 'bg-gray-300 dark:bg-gray-600'
  const canSubmit = canWrite && Boolean(selectedConnectorId)

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Cog6ToothIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">
              Data source
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Redshift connector used by this applet.
            </p>
          </div>
        </div>
        <Badge variant={!canWrite ? 'secondary' : getConfigVariant(config)}>
          {!canWrite ? 'Restricted' : configLoading ? 'Loading' : formatMode(config?.mode)}
        </Badge>
      </div>

      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-dark-border dark:bg-dark-bg">
        <div className="flex items-start gap-3">
          <span className={cn('mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full', statusTone)} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-charcoal dark:text-gray-100">
              {statusText}
            </p>
            <div className="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-400">
              {!canWrite ? (
                <p>Write permission required</p>
              ) : (
                <>
                  <p>
                    Schema{' '}
                    <span className="font-mono text-charcoal dark:text-gray-200">
                      {config?.redshift_schema || schema || DEFAULT_REDSHIFT_SCHEMA}
                    </span>
                  </p>
                  {config?.host && (
                    <p className="truncate">
                      Host{' '}
                      <span className="font-mono text-charcoal dark:text-gray-200">
                        {config.host}
                      </span>
                    </p>
                  )}
                  {config?.connector_type && (
                    <p>
                      Type{' '}
                      <span className="font-mono text-charcoal dark:text-gray-200">
                        {config.connector_type}
                      </span>
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {!canWrite ? (
        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          Requires bank reconciliation write access.
        </p>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Redshift connector
              </Label>
              {connectorsLoading && (
                <span className="text-xs text-gray-400 dark:text-gray-500">Loading</span>
              )}
            </div>

            {connectorsLoading ? (
              <div className="space-y-2">
                {[0, 1].map(item => (
                  <div
                    key={item}
                    className="h-[58px] animate-pulse rounded-lg bg-gray-100 dark:bg-dark-surface-2"
                  />
                ))}
              </div>
            ) : connectors.length ? (
              <div className="space-y-2" role="radiogroup" aria-label="Redshift connector">
                {connectors.map(connector => {
                  const selected = connector.connector_id === selectedConnectorId
                  return (
                    <button
                      key={connector.connector_id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => onConnectorChange(connector.connector_id)}
                      className={cn(
                        'w-full rounded-lg border px-3 py-3 text-left transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eliza-red/30',
                        selected
                          ? 'border-eliza-red bg-red-50/70 dark:bg-red-950/20'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:hover:bg-dark-surface-2',
                      )}
                    >
                      <span className="flex items-start justify-between gap-3">
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-charcoal dark:text-gray-100">
                            {connector.connector_name}
                          </span>
                          <span className="mt-1 block truncate font-mono text-xs text-gray-500 dark:text-gray-400">
                            {connector.connector_id}
                          </span>
                        </span>
                        <span
                          className={cn(
                            'mt-1 inline-flex h-2.5 w-2.5 flex-shrink-0 rounded-full',
                            connector.is_healthy ? 'bg-emerald-500' : 'bg-amber-500',
                          )}
                          title={connector.is_healthy ? 'Healthy' : 'Needs health check'}
                        />
                      </span>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-3 dark:border-dark-border dark:bg-dark-bg">
                <p className="text-sm font-medium text-charcoal dark:text-gray-100">
                  No Redshift connectors available.
                </p>
                <a
                  href="/data-connections"
                  className={cn(buttonVariants({ variant: 'link', size: 'sm' }), 'mt-2 h-auto px-0')}
                >
                  Open Data Connections
                </a>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="bank-reconciliation-redshift-schema"
              className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              Redshift schema
            </Label>
            <Input
              id="bank-reconciliation-redshift-schema"
              value={schema}
              placeholder={DEFAULT_REDSHIFT_SCHEMA}
              onChange={event => onSchemaChange(event.target.value)}
            />
          </div>

          {activeConnector && (
            <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">
              Selected {activeConnector.connector_name}
              {activeConnector.last_health_check
                ? ` - checked ${new Date(activeConnector.last_health_check).toLocaleDateString()}`
                : ''}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="brand"
              disabled={!canSubmit || saving}
              onClick={onSave}
            >
              <LinkIcon className="h-4 w-4" />
              {saving ? 'Saving' : 'Save & activate'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={!canSubmit || testing}
              onClick={onTest}
            >
              <ArrowPathIcon className={cn('h-4 w-4', testing && 'animate-spin')} />
              {testing ? 'Testing' : 'Test'}
            </Button>
            {config?.mode === 'saved' && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={clearing}
                onClick={onClear}
              >
                <TrashIcon className="h-4 w-4" />
                {clearing ? 'Clearing' : 'Clear'}
              </Button>
            )}
          </div>

          {message && (
            <p className={cn('rounded-lg border px-3 py-2 text-xs leading-5', getMessageClasses(message.tone))}>
              {message.text}
            </p>
          )}
        </div>
      )}
    </section>
  )
}

function MetricStrip({
  residual,
  closeKind,
  items,
}: {
  residual: number
  closeKind: ReturnType<typeof getCloseStatus>['kind']
  items: Kpi[]
}) {
  const varianceTone =
    closeKind === 'reconciled'
      ? 'text-emerald-600 dark:text-emerald-300'
      : 'text-amber-600 dark:text-amber-300'

  return (
    <div className="grid gap-3 lg:grid-cols-[1.3fr_repeat(5,minmax(0,1fr))]">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 dark:border-amber-800 dark:bg-amber-950/30">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-300" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
              Residual variance
            </p>
            <p className={cn('mt-1 font-mono text-2xl font-semibold', varianceTone)}>
              {formatCurrency(Math.abs(residual), { compact: true })}
            </p>
            <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
              GL is high until controller decisions clear the five reconciling items.
            </p>
          </div>
        </div>
      </div>

      {items.map(kpi => (
        <div
          key={kpi.label}
          className="rounded-lg border border-gray-200 bg-white px-4 py-4 dark:border-dark-border dark:bg-dark-surface"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {kpi.label}
          </p>
          <p className="mt-2 truncate font-mono text-base font-semibold text-charcoal dark:text-gray-100">
            {kpi.value}
          </p>
        </div>
      ))}
    </div>
  )
}

function WorkflowRail({ exported }: { exported: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-dark-border dark:bg-dark-surface">
      {workflowSteps.map((step, index) => {
        const isDone = exported ? index <= 6 : index < 4
        const isActive = exported ? index === 7 : index === 4
        return (
          <div key={step} className="flex items-center gap-2">
            <span
              className={cn(
                'h-2.5 w-2.5 rounded-full',
                isDone && 'bg-emerald-500',
                isActive && 'bg-eliza-red',
                !isDone && !isActive && 'bg-gray-300 dark:bg-gray-600',
              )}
            />
            <span
              className={cn(
                'text-xs font-medium',
                isDone || isActive
                  ? 'text-charcoal dark:text-gray-100'
                  : 'text-gray-400 dark:text-gray-500',
              )}
            >
              {step}
            </span>
            {index < workflowSteps.length - 1 && (
              <span className="text-gray-300 dark:text-gray-600">/</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

function ReconciliationColumn({
  title,
  rows,
  totalLabel,
  totalAmount,
}: {
  title: string
  rows: ReconciliationRow[]
  totalLabel: string
  totalAmount: number
}) {
  return (
    <div className="min-w-0">
      <div className="mb-3 flex items-center gap-2">
        <ScaleIcon className="h-4 w-4 text-gray-400" />
        <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">{title}</h3>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-dark-border/70">
        {rows.map(row => (
          <div key={row.label} className="flex items-center justify-between gap-4 py-2">
            <span
              className={cn(
                'min-w-0 text-sm',
                row.indent && 'pl-4 text-gray-500 dark:text-gray-400',
                row.tone === 'negative' && 'text-rose-700 dark:text-rose-300',
                row.tone === 'positive' && 'text-emerald-700 dark:text-emerald-300',
                !row.tone && !row.indent && 'text-charcoal dark:text-gray-100',
              )}
            >
              {row.label}
            </span>
            <span className="whitespace-nowrap font-mono text-sm font-semibold text-charcoal dark:text-gray-100">
              {formatSignedCurrency(row.amount)}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between gap-4 border-t-2 border-charcoal pt-3 dark:border-gray-200">
        <span className="text-sm font-semibold uppercase tracking-wide text-charcoal dark:text-gray-100">
          {totalLabel}
        </span>
        <span className="whitespace-nowrap font-mono text-sm font-semibold text-charcoal dark:text-gray-100">
          {formatCurrency(totalAmount)}
        </span>
      </div>
    </div>
  )
}

function JournalPreview({ lines, memo }: { lines: JournalLine[]; memo: string }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
      <div className="grid grid-cols-[76px_minmax(140px,1fr)_minmax(180px,1.6fr)_112px_112px] gap-3 border-b border-gray-200 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:border-dark-border dark:text-gray-400">
        <span>Line</span>
        <span>Account</span>
        <span>Description</span>
        <span className="text-right">Debit</span>
        <span className="text-right">Credit</span>
      </div>
      <div className="divide-y divide-gray-200 dark:divide-dark-border">
        {lines.map((line, index) => (
          <div
            key={`${line.entry}-${index}`}
            className="grid grid-cols-[76px_minmax(140px,1fr)_minmax(180px,1.6fr)_112px_112px] gap-3 px-4 py-2 text-xs"
          >
            <span className="font-mono text-gray-500 dark:text-gray-400">{line.entry}</span>
            <span className="font-medium text-charcoal dark:text-gray-100">{line.account}</span>
            <span className="text-gray-500 dark:text-gray-400">{line.description}</span>
            <span className="text-right font-mono font-semibold text-emerald-700 dark:text-emerald-300">
              {line.debit ? formatCurrency(line.debit) : ''}
            </span>
            <span className="text-right font-mono font-semibold text-rose-700 dark:text-rose-300">
              {line.credit ? formatCurrency(line.credit) : ''}
            </span>
          </div>
        ))}
      </div>
      <div className="border-t border-gray-200 px-4 py-2 text-xs text-gray-500 dark:border-dark-border dark:text-gray-400">
        {memo}
      </div>
    </div>
  )
}

function DecisionCard({
  item,
  decision,
  error,
  disabled,
  onFieldChange,
  onDecision,
}: {
  item: DecisionItem
  decision: DecisionsState[number]
  error?: string
  disabled: boolean
  onFieldChange: (id: number, patch: Partial<DecisionsState[number]>) => void
  onDecision: (item: DecisionItem, status: DecisionStatus) => void
}) {
  return (
    <article className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="grid gap-0 border-b border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2 lg:grid-cols-[88px_minmax(0,1fr)_180px_150px]">
        <div
          className={cn(
            'flex items-center justify-center border-b border-gray-200 px-3 py-3 text-xs font-semibold lg:border-b-0 lg:border-r dark:border-dark-border',
            item.priority === 'HIGH'
              ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300'
              : 'bg-sky-50 text-sky-700 dark:bg-sky-950/30 dark:text-sky-300',
          )}
        >
          {item.priority}
        </div>
        <div className="min-w-0 px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">
              {item.title}
            </h3>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 font-mono text-[11px] text-gray-500 dark:bg-dark-bg dark:text-gray-400">
              {item.code}
            </span>
          </div>
          <p className="mt-1 max-w-4xl text-xs leading-5 text-gray-500 dark:text-gray-400">
            {item.summary}
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-3 lg:border-l lg:border-t-0 dark:border-dark-border">
          <p className="font-mono text-sm font-semibold text-charcoal dark:text-gray-100">
            {formatCurrency(decision.amount)}
          </p>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {item.amountLabel}
          </p>
        </div>
        <div className="flex items-center border-t border-gray-200 px-4 py-3 lg:border-l lg:border-t-0 dark:border-dark-border">
          <span
            className={cn(
              'inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold',
              statusClasses[decision.status],
            )}
          >
            {statusLabels[decision.status]}
          </span>
        </div>
      </div>

      <div className="grid gap-5 p-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.42fr)]">
        <div className="min-w-0 space-y-4">
          <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">{item.detail}</p>
          <div>
            <div className="mb-2 flex items-center gap-2">
              <DocumentCheckIcon className="h-4 w-4 text-gray-400" />
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {item.jeHeader}
              </p>
            </div>
            <div className="overflow-x-auto">
              <JournalPreview lines={item.jeLines} memo={item.memo} />
            </div>
          </div>
        </div>

        <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-bg">
          <Textarea
            value={decision.note}
            onChange={event => onFieldChange(item.id, { note: event.target.value })}
            placeholder={item.notePlaceholder}
            disabled={disabled}
            className="min-h-[92px] bg-white dark:bg-dark-surface"
          />
          <div className="grid gap-3 sm:grid-cols-[150px_1fr] xl:grid-cols-1">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={Number.isFinite(decision.amount) ? decision.amount : 0}
              disabled={disabled || !item.amountEditable}
              onChange={event =>
                onFieldChange(item.id, { amount: Number.parseFloat(event.target.value) || 0 })
              }
              className="font-mono"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="brand"
                disabled={disabled}
                onClick={() => onDecision(item, 'approved')}
              >
                <CheckCircleIcon className="h-4 w-4" />
                {item.approveLabel}
              </Button>
              {item.modifyLabel && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={disabled}
                  onClick={() => onDecision(item, 'modified')}
                >
                  {item.modifyLabel}
                </Button>
              )}
              <Button
                size="sm"
                variant="destructive"
                disabled={disabled}
                onClick={() => onDecision(item, 'rejected')}
              >
                <XCircleIcon className="h-4 w-4" />
                {item.rejectLabel}
              </Button>
            </div>
          </div>
          {disabled && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Recording controller decisions requires bank reconciliation write access.
            </p>
          )}
          {error && <p className="text-xs font-medium text-rose-600 dark:text-rose-300">{error}</p>}
          {decision.status !== 'pending' && (
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600 dark:border-dark-border dark:bg-dark-surface dark:text-gray-300">
              <span className="font-semibold text-charcoal dark:text-gray-100">
                {statusLabels[decision.status]}
              </span>
              {decision.timestamp ? ` at ${new Date(decision.timestamp).toLocaleTimeString()}` : ''}
              {' - '}
              {decision.note || 'No controller note entered.'}
            </div>
          )}
        </div>
      </div>
    </article>
  )
}

function AnomalySeverityBadge({ severity }: { severity: string }) {
  const variant =
    severity === 'HIGH'
      ? 'danger'
      : severity === 'MED'
        ? 'warning'
        : severity === 'LOW'
          ? 'info'
          : 'secondary'

  return <Badge variant={variant}>{severity}</Badge>
}

const EMPTY_TABLE: TabularRows = { columns: [], rows: [], row_count: 0 }

function numberValue(value: unknown, fallback = 0): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizeCloseMeta(meta?: Record<string, unknown>) {
  if (!meta) return null
  return {
    entity: String(meta.entity ?? ''),
    account: String(meta.account ?? ''),
    period: String(meta.period ?? ''),
    controller: String(meta.controller ?? ''),
    staffAccountant: String(meta.staff_accountant ?? ''),
    agent: String(meta.agent ?? ''),
    runAt: 'Live Redshift',
  }
}

function normalizeKpis(values?: Record<string, unknown>): Kpi[] {
  if (!values) return []
  return [
    { label: 'Opening Balance', value: formatCurrency(numberValue(values.opening_balance)) },
    { label: 'Total Deposits', value: formatCurrency(numberValue(values.total_deposits)) },
    { label: 'Total Disbursements', value: formatCurrency(numberValue(values.total_disbursements)) },
    { label: 'Bank Closing', value: formatCurrency(numberValue(values.bank_closing)) },
    { label: 'GL Cash Balance', value: formatCurrency(numberValue(values.gl_cash_balance)) },
  ]
}

function normalizeRows(rows: ReconciliationRow[] | undefined): ReconciliationRow[] {
  if (!rows?.length) return []
  return rows.map(row => ({
    label: row.label,
    amount: numberValue(row.amount),
    tone: row.tone,
    indent: Boolean(row.indent),
  }))
}

function normalizeDecisionItems(items?: DecisionItem[]): DecisionItem[] {
  return items ?? []
}

function normalizeAnomalyFlags(flags?: AnomalyFlag[]): AnomalyFlag[] {
  return flags ?? []
}

type BankRecViewMode = 'workpaper' | 'connections'

export default function BankReconciliationPage() {
  const { hasAnyPermission } = useAuth()
  const period = 'Apr 2025'
  const canWrite = hasAnyPermission(['bank_reconciliation:write', 'platform:admin'])
  const canExport = hasAnyPermission(['bank_reconciliation:export', 'platform:admin'])
  const workpaperQuery = useBankReconciliationWorkpaper(period)
  const configQuery = useBankReconciliationDataSourceConfig(canWrite)
  const connectorsQuery = useBankReconciliationAvailableConnectors(canWrite)
  const agentRun = useRunBankReconciliationAgent()
  const setDataSource = useSetBankReconciliationDataSource()
  const testDataSource = useTestBankReconciliationDataSource()
  const clearDataSource = useClearBankReconciliationDataSource()
  const workpaper = workpaperQuery.data

  const resolvedCloseMeta = useMemo(() => normalizeCloseMeta(workpaper?.close_meta), [workpaper?.close_meta])
  const resolvedKpis = useMemo(() => normalizeKpis(workpaper?.kpis), [workpaper?.kpis])
  const resolvedBankRows = useMemo(() => normalizeRows(workpaper?.bank_rows), [workpaper?.bank_rows])
  const resolvedBookRows = useMemo(() => normalizeRows(workpaper?.book_rows), [workpaper?.book_rows])
  const resolvedDecisionItems = useMemo(
    () => normalizeDecisionItems(workpaper?.decision_items),
    [workpaper?.decision_items],
  )
  const resolvedAnomalyFlags = useMemo(
    () => normalizeAnomalyFlags(workpaper?.anomaly_flags),
    [workpaper?.anomaly_flags],
  )
  const rawVariance = numberValue(workpaper?.variance_summary?.raw_variance, 0)
  const reconciledBalance = numberValue(workpaper?.kpis?.bank_closing, 0)
  const hasLiveData = Boolean(workpaper)
  const bankTransactionsTable = workpaper?.source_tables?.bank_transactions ?? EMPTY_TABLE
  const glCashEntriesTable = workpaper?.source_tables?.gl_cash_entries ?? EMPTY_TABLE

  const [decisions, setDecisions] = useState<DecisionsState>(() => buildInitialDecisions(resolvedDecisionItems))
  const [errors, setErrors] = useState<FieldErrors>({})
  const [exported, setExported] = useState(false)
  const [selectedConnectorId, setSelectedConnectorId] = useState('')
  const [redshiftSchema, setRedshiftSchema] = useState(DEFAULT_REDSHIFT_SCHEMA)
  const [configMessage, setConfigMessage] = useState<ConfigMessage | null>(null)
  const [viewMode, setViewMode] = useState<BankRecViewMode>('workpaper')

  useEffect(() => {
    setDecisions(buildInitialDecisions(resolvedDecisionItems))
    setErrors({})
    setExported(false)
  }, [resolvedDecisionItems])

  useEffect(() => {
    if (!canWrite) {
      setSelectedConnectorId('')
      setRedshiftSchema(DEFAULT_REDSHIFT_SCHEMA)
      return
    }

    const config = configQuery.data
    if (!config) return
    setSelectedConnectorId(config.connector_id ?? '')
    setRedshiftSchema(config.redshift_schema || DEFAULT_REDSHIFT_SCHEMA)
  }, [canWrite, configQuery.data])

  const counts = useMemo(() => computeDecisionCounts(decisions), [decisions])
  const residual = useMemo(
    () => computeResidualVariance(decisions, resolvedDecisionItems, rawVariance),
    [decisions, resolvedDecisionItems, rawVariance],
  )
  const closeStatus = useMemo(
    () => getCloseStatus(decisions, resolvedDecisionItems, rawVariance),
    [decisions, resolvedDecisionItems, rawVariance],
  )
  const exportReady = areAllDecisionsResolved(decisions)

  const updateDecisionField = (id: number, patch: Partial<DecisionsState[number]>) => {
    if (!canWrite) return
    setDecisions(current => ({
      ...current,
      [id]: {
        ...(current[id] ?? { status: 'pending', amount: 0, note: '' }),
        ...patch,
      },
    }))
    setErrors(current => ({ ...current, [id]: undefined }))
  }

  const recordDecision = (item: DecisionItem, status: DecisionStatus) => {
    if (!canWrite) return
    const current = decisions[item.id] ?? { status: 'pending', amount: item.amount, note: '' }
    const needsNote = status === 'approved' || status === 'modified'

    if (needsNote && !current.note.trim()) {
      setErrors(prev => ({
        ...prev,
        [item.id]: 'A controller note is required before approving or modifying.',
      }))
      return
    }

    if ((status === 'approved' || status === 'modified') && current.amount <= 0) {
      setErrors(prev => ({
        ...prev,
        [item.id]: 'Enter an amount greater than zero before recording this decision.',
      }))
      return
    }

    setDecisions(prev => ({
      ...prev,
      [item.id]: {
        ...(prev[item.id] ?? current),
        status,
        note:
          current.note.trim() ||
          (status === 'rejected' ? 'Rejected - carry as timing item.' : current.note),
        timestamp: new Date().toISOString(),
      },
    }))
    setErrors(prev => ({ ...prev, [item.id]: undefined }))
    setExported(false)
  }

  const generateExports = () => {
    if (!exportReady || !canExport) return
    downloadTextFile('EC07_JE_Apr2025_NetSuite.csv', generateNetSuiteCsv(decisions, resolvedDecisionItems))
    setTimeout(() => {
      downloadTextFile('EC07_AuditTrail_Apr2025.csv', generateAuditTrailCsv(decisions, resolvedDecisionItems))
    }, 250)
    setExported(true)
  }

  const buildDataSourcePayload = () => ({
    connector_id: selectedConnectorId,
    redshift_schema: redshiftSchema.trim() || DEFAULT_REDSHIFT_SCHEMA,
  })

  const testSelectedDataSource = () => {
    if (!canWrite || !selectedConnectorId) {
      setConfigMessage({ tone: 'error', text: 'Select a Redshift connector first.' })
      return
    }

    setConfigMessage(null)
    testDataSource.mutate(buildDataSourcePayload(), {
      onSuccess: result => {
        setConfigMessage({
          tone: result.redshift_connected ? 'success' : 'error',
          text: result.message || (result.redshift_connected ? 'Connection healthy.' : 'Connection failed.'),
        })
      },
      onError: error => {
        setConfigMessage({ tone: 'error', text: getErrorMessage(error) })
      },
    })
  }

  const saveSelectedDataSource = () => {
    if (!canWrite || !selectedConnectorId) {
      setConfigMessage({ tone: 'error', text: 'Select a Redshift connector first.' })
      return
    }

    setConfigMessage(null)
    setDataSource.mutate(buildDataSourcePayload(), {
      onSuccess: updatedConfig => {
        setSelectedConnectorId(updatedConfig.connector_id ?? '')
        setRedshiftSchema(updatedConfig.redshift_schema || DEFAULT_REDSHIFT_SCHEMA)
        setConfigMessage({
          tone: 'success',
          text: `${updatedConfig.connector_name || 'Redshift connector'} is active for bank reconciliation.`,
        })
      },
      onError: error => {
        setConfigMessage({ tone: 'error', text: getErrorMessage(error) })
      },
    })
  }

  const clearSelectedDataSource = () => {
    if (!canWrite) return
    if (!window.confirm('Clear the saved Redshift connector for Bank Reconciliation?')) return

    setConfigMessage(null)
    clearDataSource.mutate(undefined, {
      onSuccess: updatedConfig => {
        setSelectedConnectorId(updatedConfig.connector_id ?? '')
        setRedshiftSchema(updatedConfig.redshift_schema || DEFAULT_REDSHIFT_SCHEMA)
        setConfigMessage({
          tone: 'info',
          text:
            updatedConfig.mode === 'env_fallback'
              ? 'Saved connector cleared. Environment fallback is active.'
              : 'Saved connector cleared.',
        })
      },
      onError: error => {
        setConfigMessage({ tone: 'error', text: getErrorMessage(error) })
      },
    })
  }

  const statusBadgeVariant =
    closeStatus.kind === 'reconciled'
      ? 'success'
      : closeStatus.kind === 'timing'
        ? 'warning'
        : 'danger'

  return (
    <Page layout="full-width">
      <PageHeader
        title="Bank Reconciliation"
        description="EC-07 controller console for resolving cash-to-bank variances and exporting NetSuite journal entries."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => agentRun.mutate({ period })}
              disabled={!canWrite || agentRun.isPending}
              variant="secondary"
              title={canWrite ? undefined : 'Requires bank reconciliation write access'}
            >
              <ScaleIcon className="h-4 w-4" />
              {agentRun.isPending ? 'Running agent' : 'Run agent'}
            </Button>
            <Button
              onClick={generateExports}
              disabled={!exportReady || !canExport}
              variant="brand"
              title={canExport ? undefined : 'Requires bank reconciliation export access'}
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              Generate exports
            </Button>
          </div>
        }
      />
      {/* Tab strip */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface px-8">
        <div className="flex gap-0">
          {(['workpaper', 'connections'] as BankRecViewMode[]).map(tab => (
            <button
              key={tab}
              onClick={() => setViewMode(tab)}
              className={cn(
                'relative px-4 py-3 text-sm font-medium capitalize transition-colors duration-150',
                viewMode === tab
                  ? 'text-charcoal dark:text-gray-100'
                  : 'text-gray-500 dark:text-gray-400 hover:text-charcoal dark:hover:text-gray-200',
              )}
            >
              {tab === 'connections' ? 'Connections' : 'Workpaper'}
              {tab === 'connections' && !hasLiveData && (
                <span className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 text-[9px] font-bold text-white">
                  !
                </span>
              )}
              {viewMode === tab && (
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-eliza-red rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {viewMode === 'connections' ? (
        <PageBody className="pb-8">
          <div className="max-w-2xl">
            <div className="mb-5">
              <h2 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-1">
                Data Source
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Select the Redshift connector and schema that back this applet. Changes take effect on the next agent run.
              </p>
            </div>
            <DataSourceConfigPanel
              config={canWrite ? configQuery.data : undefined}
              configLoading={canWrite && configQuery.isLoading}
              connectors={canWrite ? connectorsQuery.data ?? [] : []}
              connectorsLoading={canWrite && connectorsQuery.isLoading}
              selectedConnectorId={selectedConnectorId}
              schema={redshiftSchema}
              canWrite={canWrite}
              message={configMessage}
              saving={setDataSource.isPending}
              testing={testDataSource.isPending}
              clearing={clearDataSource.isPending}
              onConnectorChange={connectorId => {
                setSelectedConnectorId(connectorId)
                setConfigMessage(null)
              }}
              onSchemaChange={nextSchema => {
                setRedshiftSchema(nextSchema)
                setConfigMessage(null)
              }}
              onTest={testSelectedDataSource}
              onSave={saveSelectedDataSource}
              onClear={clearSelectedDataSource}
            />
          </div>
        </PageBody>
      ) : (
      <PageBody className="space-y-6 pb-8">
        <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
          <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant={statusBadgeVariant}>{closeStatus.label}</Badge>
                <Badge variant={hasLiveData ? 'success' : workpaperQuery.isLoading ? 'secondary' : 'warning'}>
                  {hasLiveData ? 'Redshift live' : workpaperQuery.isLoading ? 'Loading…' : 'No data source'}
                </Badge>
                {resolvedCloseMeta && (
                  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                    Run {resolvedCloseMeta.runAt}
                  </span>
                )}
              </div>
              <h2 className="mt-4 max-w-4xl text-2xl font-semibold tracking-normal text-charcoal dark:text-gray-100">
                {resolvedCloseMeta?.entity ?? '—'} needs {resolvedDecisionItems.length} controller decisions before close can move.
              </h2>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                The agent reconciled bank and book balances to {formatCurrency(reconciledBalance)} and isolated a {formatCurrency(Math.abs(residual))} residual variance for HITL disposition.
              </p>
              {agentRun.data && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Agent status {agentRun.data.status}
                  {agentRun.data.trace_id ? ` - Langfuse trace ${agentRun.data.trace_id}` : ''}
                </p>
              )}
              {workpaperQuery.isError && (
                <p className="mt-2 text-xs text-rose-700 dark:text-rose-400">
                  Redshift data unavailable. Configure the connector in Settings.
                </p>
              )}
            </div>
            {resolvedCloseMeta && (
              <dl className="grid gap-3 rounded-lg bg-gray-50 p-4 text-sm dark:bg-dark-bg">
                {[
                  ['Entity', resolvedCloseMeta.entity],
                  ['Account', resolvedCloseMeta.account],
                  ['Period', resolvedCloseMeta.period],
                  ['Controller', resolvedCloseMeta.controller],
                  ['Staff accountant', resolvedCloseMeta.staffAccountant],
                ].map(([label, value]) => (
                  <div key={label} className="grid grid-cols-[118px_1fr] gap-3">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {label}
                    </dt>
                    <dd className="min-w-0 truncate text-charcoal dark:text-gray-100">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </section>

        <WorkflowRail exported={exported} />
        <MetricStrip residual={residual} closeKind={closeStatus.kind} items={resolvedKpis} />

        <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-5">
            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100">
                    Formal reconciliation
                  </h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Bank side and book side tie after all identified variance drivers are applied.
                  </p>
                </div>
                <Badge variant={Math.abs(residual) <= 1 ? 'success' : 'warning'}>
                  {closeStatus.description}
                </Badge>
              </div>
              <div className="grid gap-6 lg:grid-cols-2">
                <ReconciliationColumn
                  title="Bank side - Pacific Commerce (10100)"
                  rows={resolvedBankRows}
                  totalLabel="Reconciled bank balance"
                  totalAmount={reconciledBalance}
                />
                <ReconciliationColumn
                  title="Book side - GL account 10100"
                  rows={resolvedBookRows}
                  totalLabel="Reconciled book balance"
                  totalAmount={reconciledBalance}
                />
              </div>
            </div>

            <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100">
                    Source data explorer
                  </h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Redshift-backed source rows use the platform tabular viewer for search, sort, pagination, and CSV/Excel/JSON export.
                  </p>
                </div>
                <Badge variant={hasLiveData ? 'success' : workpaperQuery.isLoading ? 'secondary' : 'warning'}>
                  {hasLiveData ? 'Connected' : workpaperQuery.isLoading ? 'Loading…' : 'No data source'}
                </Badge>
              </div>
              <Tabs defaultValue="bank">
                <TabsList variant="underline" className="w-full overflow-x-auto">
                  <TabsTrigger value="bank" variant="underline">Bank statement</TabsTrigger>
                  <TabsTrigger value="gl" variant="underline">GL cash entries</TabsTrigger>
                </TabsList>
                <TabsContent value="bank">
                  <TabularDataViewer
                    data={bankTransactionsTable}
                    fileBaseName="bank-reconciliation-bank-transactions"
                    loading={workpaperQuery.isLoading && !workpaper}
                    emptyMessage="Connect Redshift to preview bank statement rows."
                    maxHeight="360px"
                  />
                </TabsContent>
                <TabsContent value="gl">
                  <TabularDataViewer
                    data={glCashEntriesTable}
                    fileBaseName="bank-reconciliation-gl-cash-entries"
                    loading={workpaperQuery.isLoading && !workpaper}
                    emptyMessage="Connect Redshift to preview GL cash rows."
                    maxHeight="360px"
                  />
                </TabsContent>
              </Tabs>
            </section>

            <div className="space-y-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100">
                    Controller decision queue
                  </h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Approve, modify, or reject each item. Approved and modified items generate NetSuite JE lines.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="warning">Pending {counts.pending}</Badge>
                  <Badge variant="success">Approved {counts.approved}</Badge>
                  <Badge variant="info">Modified {counts.modified}</Badge>
                  <Badge variant="danger">Rejected {counts.rejected}</Badge>
                </div>
              </div>

              {resolvedDecisionItems.map(item => (
                <DecisionCard
                  key={item.id}
                  item={item}
                  decision={decisions[item.id] ?? { status: 'pending', amount: item.amount, note: '' }}
                  error={errors[item.id]}
                  disabled={!canWrite}
                  onFieldChange={updateDecisionField}
                  onDecision={recordDecision}
                />
              ))}
            </div>
          </div>

          <aside className="space-y-5 xl:sticky xl:top-4 xl:self-start">
            <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
              <div className="mb-4 flex items-center gap-2">
                <ClockIcon className="h-5 w-5 text-gray-400" />
                <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">
                  Close status
                </h2>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Residual</span>
                  <span className="font-mono text-sm font-semibold text-charcoal dark:text-gray-100">
                    {formatCurrency(Math.abs(closeStatus.residual))}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Decisions</span>
                  <span className="font-mono text-sm font-semibold text-charcoal dark:text-gray-100">
                    {resolvedDecisionItems.length - counts.pending}/{resolvedDecisionItems.length}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Export package</span>
                  <span className="font-mono text-sm font-semibold text-charcoal dark:text-gray-100">
                    {exported ? 'Generated' : exportReady ? 'Ready' : 'Waiting'}
                  </span>
                </div>
              </div>
              <div
                className={cn(
                  'mt-5 rounded-lg border px-4 py-3 text-sm leading-6',
                  closeStatus.kind === 'reconciled'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200'
                    : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200',
                )}
              >
                {closeStatus.description}
              </div>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
              <div className="mb-4 flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-gray-400" />
                <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">
                  Anomaly flags
                </h2>
              </div>
              <div className="space-y-3">
                {resolvedAnomalyFlags.map(flag => (
                  <div
                    key={flag.transactionId}
                    className="border-b border-gray-100 pb-3 last:border-0 last:pb-0 dark:border-dark-border"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <AnomalySeverityBadge severity={flag.severity} />
                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                        {flag.transactionId}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-medium text-charcoal dark:text-gray-100">
                      {flag.description}
                    </p>
                    <p className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400">
                      {formatCurrency(flag.amount)}
                    </p>
                    <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
                      {flag.action}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
              <div className="mb-4 flex items-center gap-2">
                <ArrowDownTrayIcon className="h-5 w-5 text-gray-400" />
                <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">
                  Source package
                </h2>
              </div>
              <div className="space-y-3">
                {hasLiveData ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Source tables are available in the Raw Data tab above.
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Connect a Redshift data source to view source files.
                  </p>
                )}
              </div>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
              <div className="mb-4 flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
                <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">
                  Certification
                </h2>
              </div>
              <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">
                Marlowe Mitchell certifies the bank statement balance, controller decisions, approved NetSuite entries, and final variance status after the queue is resolved.
              </p>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between gap-3 border-b border-gray-200 pb-2 dark:border-dark-border">
                  <span className="text-gray-500 dark:text-gray-400">Variance resolution</span>
                  <span className="text-right font-medium text-charcoal dark:text-gray-100">
                    {closeStatus.kind === 'blocked'
                      ? 'Pending decisions'
                      : closeStatus.kind === 'reconciled'
                        ? '$0 unexplained variance'
                        : `${formatCurrency(Math.abs(residual))} carried forward`}
                  </span>
                </div>
                <div className="flex justify-between gap-3 border-b border-gray-200 pb-2 dark:border-dark-border">
                  <span className="text-gray-500 dark:text-gray-400">Final GL cash balance</span>
                  <span className="font-mono font-medium text-charcoal dark:text-gray-100">
                    {closeStatus.kind === 'blocked' ? 'Pending' : formatCurrency(reconciledBalance)}
                  </span>
                </div>
              </div>
            </section>

            <a
              href="/bank-reconciliation/EC07_BankRec_Apr2025_Interactive.html"
              target="_blank"
              rel="noreferrer"
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-full')}
            >
              Open original HTML
            </a>
          </aside>
        </section>
      </PageBody>
      )}
    </Page>
  )
}
